import { Icon, IconClose, IconMagnifier } from "@flixbus/honeycomb-icons-react";
import { Button, Heading, Input } from "@flixbus/honeycomb-react";
import { DefaultInfoAlert, normalizeText } from "@flixbus-phx/marketplace-common";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import BookingDataAccessCheck from "../shared/components/bookingDataAccessCheck/BookingDataAccessCheck";
import { RidePassengerStatus } from "../shared/types/schema";
import * as css from "./PaxList.scss";
import { useFindRidePaxQuery } from "./api/operations.generated";
import PaxListStationRow from "./container/paxListStationRow/PaxListStationRow";
import PaxListLoading from "./container/ui/paxListLoading/PaxListLoading";
import PaxListNavigation from "./container/ui/paxListNavigation/PaxListNavigation";
import { QueriedRidePassenger } from "./types";

const POLL_INTERVAL_MILLIS = 30_000;

const PaxList: React.FC = () => {
  const { formatMessage, formatPlural } = useIntl();
  const { rideId } = useParams();
  const [searchValue, setSearchValue] = React.useState("");
  const [paxList, setPaxList] = React.useState<QueriedRidePassenger[]>([]);

  const { data, error, loading } = useFindRidePaxQuery({
    variables: { id: rideId! },
    pollInterval: POLL_INTERVAL_MILLIS,
  });

  React.useEffect(() => {
    if (data) {
      setPaxList(
        data.getRide.ridePassengers
          .filter((pax) => {
            return normalizeText(pax.name).includes(normalizeText(searchValue));
          })
          .filter((pax) => pax.status === RidePassengerStatus.Paid)
      );
    }
  }, [searchValue, data?.getRide.ridePassengers]);

  return (
    <BookingDataAccessCheck LoadingAnimation={<PaxListLoading />}>
      {loading && <PaxListLoading />}
      {error && <DefaultInfoAlert message={formatMessage({ id: "general.AnErrorOccured" })} />}
      {data?.getRide && (
        <>
          <div className={css.wrapper}>
            <Heading size={3} sectionHeader>
              <FormattedMessage id="checkIn.paxList.headline" />
            </Heading>
            <div
              className={css.paxCount}
              dangerouslySetInnerHTML={{
                __html: formatMessage(
                  {
                    id: `checkIn.paxList.passengerCount.${formatPlural(paxList.length)}`,
                  },
                  {
                    countPax: paxList?.length,
                  },
                  { ignoreTag: true }
                ),
              }}
            />
            <Input
              id="paxSearch"
              aria-label="Search Passengers"
              placeholder={formatMessage({ id: "checkIn.paxList.search.placeholder" })}
              iconLeft={<Icon InlineIcon={IconMagnifier} extraClasses={css.icon} />}
              iconRight={
                searchValue.length ? (
                  <Button aria-label="Clear search" onClick={() => setSearchValue("")}>
                    <Icon InlineIcon={IconClose} extraClasses={css.icon} />
                  </Button>
                ) : undefined
              }
              extraClasses={css.input}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            {data.getRide.route.map((routeEntry) => {
              const { station } = routeEntry;

              return (
                <PaxListStationRow
                  key={station.id}
                  stationName={station.name}
                  paxList={// eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
                  paxList?.filter((pax) => pax.departureStationId === station.id)!}
                  readOnly={!data.getRide.checkInAllowed}
                />
              );
            })}
          </div>
          <PaxListNavigation />
        </>
      )}
    </BookingDataAccessCheck>
  );
};

export default PaxList;
